import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './Manage.css';

const MANAGE_BUILD_STAMP = process.env.REACT_APP_MANAGE_BUILD_STAMP || 'manage-ui-2026-07-23-1';

function Manage() {
  const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_BASE_URL) {
      return process.env.REACT_APP_API_BASE_URL;
    }

    return '/api/v1';
  };

  const parseJsonResponse = async (response) => {
    const contentType = response.headers.get('content-type') || '';

    if (!contentType.includes('application/json')) {
      const text = await response.text();
      throw new Error(text.slice(0, 120) || 'Expected JSON response');
    }

    return response.json();
  };

  const getAuthToken = () => localStorage.getItem('auth_token');

  const fetchWithAuth = async (url, options = {}) => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Missing authentication token');
    }

    return fetch(url, {
      ...options,
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`
      }
    });
  };

  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [allBuckets, setAllBuckets] = useState([]);
  const [allBucketsLoading, setAllBucketsLoading] = useState(false);
  const [allBucketsError, setAllBucketsError] = useState('');
  const [operations, setOperations] = useState([]);
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [operationsError, setOperationsError] = useState('');
  const [buckets, setBuckets] = useState([]);
  const [bucketsLoading, setBucketsLoading] = useState(false);
  const [bucketsError, setBucketsError] = useState('');

  const [userForm, setUserForm] = useState({ username: '', password: '', role: 'user', customer_id: '' });
  const [customerForm, setCustomerForm] = useState({
    name: '',
    address: '',
    email: '',
    phonenumber: '',
    organization_code: '',
    customer_type: ''
  });
  const [bucketForm, setBucketForm] = useState({ serial_number: '', bucket_type: '', customer_id: '' });
  const [bucketOperationForm, setBucketOperationForm] = useState({
    customer_id: '',
    bucket_id: '',
    operation_start: '',
    operation_end: '',
    notes: ''
  });

  const [userStatus, setUserStatus] = useState('');
  const [userError, setUserError] = useState('');
  const [customerStatus, setCustomerStatus] = useState('');
  const [customerError, setCustomerError] = useState('');
  const [bucketStatus, setBucketStatus] = useState('');
  const [bucketError, setBucketError] = useState('');
  const [operationStatus, setOperationStatus] = useState('');
  const [operationError, setOperationError] = useState('');

  const selectedOperationCustomerId = useMemo(() => Number(bucketOperationForm.customer_id) || 0, [bucketOperationForm.customer_id]);

  const loadCustomers = useCallback(async () => {
    setCustomersLoading(true);
    setCustomersError('');

    try {
      const response = await fetchWithAuth(`${getApiBaseUrl()}/customers`);
      const data = await parseJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load customers');
      }

      setCustomers(data.customers || []);
    } catch (error) {
      setCustomersError(error.message || 'Failed to load customers');
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  const loadBucketsForCustomer = useCallback(async (customerId) => {
    if (!customerId) {
      setBuckets([]);
      return;
    }

    setBucketsLoading(true);
    setBucketsError('');

    try {
      const response = await fetchWithAuth(`${getApiBaseUrl()}/customers/${customerId}`);
      const data = await parseJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load buckets');
      }

      setBuckets(data.buckets || []);
    } catch (error) {
      setBucketsError(error.message || 'Failed to load buckets');
      setBuckets([]);
    } finally {
      setBucketsLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError('');

    try {
      const response = await fetchWithAuth(`${getApiBaseUrl()}/users`);
      const data = await parseJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load users');
      }

      setUsers(data.users || []);
    } catch (error) {
      setUsersError(error.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const loadAllBuckets = useCallback(async () => {
    setAllBucketsLoading(true);
    setAllBucketsError('');

    try {
      const response = await fetchWithAuth(`${getApiBaseUrl()}/buckets`);
      const data = await parseJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load buckets');
      }

      setAllBuckets(data.buckets || []);
    } catch (error) {
      setAllBucketsError(error.message || 'Failed to load buckets');
    } finally {
      setAllBucketsLoading(false);
    }
  }, []);

  const loadOperations = useCallback(async () => {
    setOperationsLoading(true);
    setOperationsError('');

    try {
      const response = await fetchWithAuth(`${getApiBaseUrl()}/bucket-operations?limit=200`);
      const data = await parseJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load bucket operations');
      }

      setOperations(data.operations || []);
    } catch (error) {
      setOperationsError(error.message || 'Failed to load bucket operations');
    } finally {
      setOperationsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
    loadUsers();
    loadAllBuckets();
    loadOperations();
  }, [loadCustomers, loadUsers, loadAllBuckets, loadOperations]);

  useEffect(() => {
    loadBucketsForCustomer(selectedOperationCustomerId);
  }, [loadBucketsForCustomer, selectedOperationCustomerId]);

  const handleUserSubmit = async (event) => {
    event.preventDefault();
    setUserStatus('');
    setUserError('');

    try {
      const payload = {
        username: userForm.username.trim(),
        password: userForm.password,
        role: userForm.role,
        ...(userForm.customer_id === '' ? {} : { customer_id: Number(userForm.customer_id) })
      };

      const response = await fetchWithAuth(`${getApiBaseUrl()}/users`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      const data = await parseJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to add user');
      }

      setUserStatus(`User ${data.user?.username || payload.username} added.`);
      setUserForm({ username: '', password: '', role: 'user', customer_id: '' });
      await loadUsers();
    } catch (error) {
      setUserError(error.message || 'Failed to add user');
    }
  };

  const handleCustomerSubmit = async (event) => {
    event.preventDefault();
    setCustomerStatus('');
    setCustomerError('');

    try {
      const payload = {
        name: customerForm.name.trim(),
        address: customerForm.address.trim() || undefined,
        email: customerForm.email.trim() || undefined,
        phonenumber: customerForm.phonenumber.trim() || undefined,
        organization_code: customerForm.organization_code.trim() || undefined,
        customer_type: customerForm.customer_type === '' ? undefined : Number(customerForm.customer_type)
      };

      const response = await fetchWithAuth(`${getApiBaseUrl()}/customers`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      const data = await parseJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to add customer');
      }

      setCustomerStatus(`Customer added with ID ${data.insertId}.`);
      setCustomerForm({
        name: '',
        address: '',
        email: '',
        phonenumber: '',
        organization_code: '',
        customer_type: ''
      });
      await loadCustomers();
    } catch (error) {
      setCustomerError(error.message || 'Failed to add customer');
    }
  };

  const handleBucketSubmit = async (event) => {
    event.preventDefault();
    setBucketStatus('');
    setBucketError('');

    try {
      const payload = {
        serial_number: bucketForm.serial_number.trim(),
        bucket_type: bucketForm.bucket_type.trim(),
        customer_id: Number(bucketForm.customer_id)
      };

      const response = await fetchWithAuth(`${getApiBaseUrl()}/buckets`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      const data = await parseJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to add bucket');
      }

      setBucketStatus(`Bucket added with ID ${data.insertId}.`);
      setBucketForm({ serial_number: '', bucket_type: '', customer_id: '' });
      await Promise.all([
        loadBucketsForCustomer(Number(bucketForm.customer_id)),
        loadAllBuckets()
      ]);
    } catch (error) {
      setBucketError(error.message || 'Failed to add bucket');
    }
  };

  const handleBucketOperationSubmit = async (event) => {
    event.preventDefault();
    setOperationStatus('');
    setOperationError('');

    try {
      const payload = {
        customer_id: Number(bucketOperationForm.customer_id),
        bucket_id: Number(bucketOperationForm.bucket_id),
        operation_start: bucketOperationForm.operation_start,
        operation_end: bucketOperationForm.operation_end,
        notes: bucketOperationForm.notes.trim() || undefined
      };

      const response = await fetchWithAuth(`${getApiBaseUrl()}/bucket-operations`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      const data = await parseJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to add bucket operation');
      }

      setOperationStatus(`Bucket operation added with ID ${data.insertId}.`);
      setBucketOperationForm({
        customer_id: '',
        bucket_id: '',
        operation_start: '',
        operation_end: '',
        notes: ''
      });
      setBuckets([]);
      await loadOperations();
    } catch (error) {
      setOperationError(error.message || 'Failed to add bucket operation');
    }
  };

  return (
    <div className="manage-page">
      <div className="manage-hero">
        <div>
          <p className="manage-eyebrow">Admin only</p>
          <h1>Manage</h1>
          <p>Add users, customers, buckets, and bucket operations from one place.</p>
        </div>
        <div className="build-stamp">Build stamp: {MANAGE_BUILD_STAMP}</div>
      </div>

      <div className="manage-grid">
        <section className="manage-card">
          <h2>Add User</h2>
          <form onSubmit={handleUserSubmit} className="manage-form">
            <input placeholder="Username" value={userForm.username} onChange={(e) => setUserForm({ ...userForm, username: e.target.value })} />
            <input placeholder="Password" type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} />
            <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            <select value={userForm.customer_id} onChange={(e) => setUserForm({ ...userForm, customer_id: e.target.value })}>
              <option value="">No customer</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.id} - {customer.name}
                </option>
              ))}
            </select>
            <button type="submit" className="btn btn-primary">Add User</button>
          </form>
          {userStatus && <div className="message success">{userStatus}</div>}
          {userError && <div className="message error">{userError}</div>}
        </section>

        <section className="manage-card">
          <h2>Add Customer</h2>
          <form onSubmit={handleCustomerSubmit} className="manage-form">
            <input placeholder="Name" value={customerForm.name} onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })} />
            <input placeholder="Address" value={customerForm.address} onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })} />
            <input placeholder="Email" value={customerForm.email} onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })} />
            <input placeholder="Phone number" value={customerForm.phonenumber} onChange={(e) => setCustomerForm({ ...customerForm, phonenumber: e.target.value })} />
            <input placeholder="Organization code" value={customerForm.organization_code} onChange={(e) => setCustomerForm({ ...customerForm, organization_code: e.target.value })} />
            <input placeholder="Customer type" type="number" min="0" max="4" value={customerForm.customer_type} onChange={(e) => setCustomerForm({ ...customerForm, customer_type: e.target.value })} />
            <button type="submit" className="btn btn-primary">Add Customer</button>
          </form>
          {customerStatus && <div className="message success">{customerStatus}</div>}
          {customerError && <div className="message error">{customerError}</div>}
        </section>

        <section className="manage-card">
          <h2>Add Bucket</h2>
          <form onSubmit={handleBucketSubmit} className="manage-form">
            <input placeholder="Serial number" value={bucketForm.serial_number} onChange={(e) => setBucketForm({ ...bucketForm, serial_number: e.target.value })} />
            <input placeholder="Bucket type" value={bucketForm.bucket_type} onChange={(e) => setBucketForm({ ...bucketForm, bucket_type: e.target.value })} />
            <select value={bucketForm.customer_id} onChange={(e) => setBucketForm({ ...bucketForm, customer_id: e.target.value })}>
              <option value="">Select customer</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.id} - {customer.name}
                </option>
              ))}
            </select>
            <button type="submit" className="btn btn-primary">Add Bucket</button>
          </form>
          {bucketStatus && <div className="message success">{bucketStatus}</div>}
          {bucketError && <div className="message error">{bucketError}</div>}
        </section>

        <section className="manage-card manage-card-wide">
          <h2>Add Bucket Operation</h2>
          <form onSubmit={handleBucketOperationSubmit} className="manage-form manage-form-grid">
            <select value={bucketOperationForm.customer_id} onChange={(e) => setBucketOperationForm({ ...bucketOperationForm, customer_id: e.target.value, bucket_id: '' })}>
              <option value="">Select customer</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.id} - {customer.name}
                </option>
              ))}
            </select>
            <select value={bucketOperationForm.bucket_id} onChange={(e) => setBucketOperationForm({ ...bucketOperationForm, bucket_id: e.target.value })} disabled={!bucketOperationForm.customer_id || bucketsLoading}>
              <option value="">{bucketsLoading ? 'Loading buckets...' : 'Select bucket'}</option>
              {buckets.map((bucket) => (
                <option key={bucket.id} value={bucket.id}>
                  {bucket.serial_number} - {bucket.bucket_type}
                </option>
              ))}
            </select>
            <input type="datetime-local" value={bucketOperationForm.operation_start} onChange={(e) => setBucketOperationForm({ ...bucketOperationForm, operation_start: e.target.value })} />
            <input type="datetime-local" value={bucketOperationForm.operation_end} onChange={(e) => setBucketOperationForm({ ...bucketOperationForm, operation_end: e.target.value })} />
            <textarea placeholder="Notes" rows="4" value={bucketOperationForm.notes} onChange={(e) => setBucketOperationForm({ ...bucketOperationForm, notes: e.target.value })} />
            <button type="submit" className="btn btn-primary">Add Operation</button>
          </form>
          {operationStatus && <div className="message success">{operationStatus}</div>}
          {operationError && <div className="message error">{operationError}</div>}
          {bucketsError && <div className="message error">{bucketsError}</div>}
        </section>

        <section className="manage-card manage-card-wide">
          <div className="manage-card-header">
            <h2>Current Customers</h2>
            <button type="button" className="btn btn-secondary" onClick={loadCustomers} disabled={customersLoading}>
              {customersLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {customersError && <div className="message error">{customersError}</div>}

          <div className="manage-table">
            {customers.map((customer) => (
              <div key={customer.id} className="manage-table-row">
                <strong>{customer.name}</strong>
                <span>ID {customer.id}</span>
                <span>{customer.organization_code || 'No org code'}</span>
              </div>
            ))}
            {!customersLoading && customers.length === 0 && <div className="manage-empty">No customers loaded.</div>}
          </div>
        </section>

        <section className="manage-card manage-card-wide">
          <div className="manage-card-header">
            <h2>Current Users</h2>
            <button type="button" className="btn btn-secondary" onClick={loadUsers} disabled={usersLoading}>
              {usersLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {usersError && <div className="message error">{usersError}</div>}

          <div className="manage-table">
            {users.map((user) => (
              <div key={user.id} className="manage-table-row manage-table-row-users">
                <strong>{user.username}</strong>
                <span>{user.role}</span>
                <span>Customer {user.customer_id}</span>
                <span>{user.created_at || 'No timestamp'}</span>
              </div>
            ))}
            {!usersLoading && users.length === 0 && <div className="manage-empty">No users loaded.</div>}
          </div>
        </section>

        <section className="manage-card manage-card-wide">
          <div className="manage-card-header">
            <h2>Current Buckets</h2>
            <button type="button" className="btn btn-secondary" onClick={loadAllBuckets} disabled={allBucketsLoading}>
              {allBucketsLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {allBucketsError && <div className="message error">{allBucketsError}</div>}

          <div className="manage-table">
            {allBuckets.map((bucket) => (
              <div key={bucket.id} className="manage-table-row manage-table-row-buckets">
                <strong>{bucket.serial_number}</strong>
                <span>{bucket.bucket_type}</span>
                <span>{bucket.customer_name || `Customer ${bucket.customer_id}`}</span>
                <span>ID {bucket.id}</span>
              </div>
            ))}
            {!allBucketsLoading && allBuckets.length === 0 && <div className="manage-empty">No buckets loaded.</div>}
          </div>
        </section>

        <section className="manage-card manage-card-wide">
          <div className="manage-card-header">
            <h2>Current Bucket Operations</h2>
            <button type="button" className="btn btn-secondary" onClick={loadOperations} disabled={operationsLoading}>
              {operationsLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {operationsError && <div className="message error">{operationsError}</div>}

          <div className="manage-table">
            {operations.map((operation) => (
              <div key={operation.id} className="manage-table-row manage-table-row-operations">
                <strong>{operation.serial_number || `Bucket ${operation.bucket_id}`}</strong>
                <span>{operation.customer_name || `Customer ${operation.customer_id}`}</span>
                <span>{operation.operation_start || '-'}</span>
                <span>{operation.operation_end || '-'}</span>
                <span>{operation.notes || '-'}</span>
              </div>
            ))}
            {!operationsLoading && operations.length === 0 && <div className="manage-empty">No bucket operations loaded.</div>}
          </div>
        </section>
      </div>
    </div>
  );
}

export default Manage;